import { useCallback, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import { DECIMAL_PLACES } from "@constants/numbers";
import { slotType, vatHandlingType } from "@enums/hooks.enum";
import { calculationTypes, DateFormats } from "@enums/shared.enum";
import { useBooking } from "@hooks/useBooking/useBooking";
import { ResourcePricesResponse } from "@models/components/booking.model";
import {
  calculateArgs,
  selectedSlotType,
  specialSlotType,
} from "@models/hooks/usePricing.model";
import {
  BookingFetchResponseType,
  QuotaUpdateResponse,
} from "@models/services/booking";
import {
  CalculatePriceApi,
  PriceSlotDurationType,
} from "@models/services/pricing";
import handleResponse from "@services/apiConfig/api.response.service";
import API_SERVICES from "@services/index";
import bookingSlice from "@slices/booking.slice";
import { RootState } from "@store/index";

export const usePricing = () => {
  const dispatch = useDispatch();

  const [priceLoader, setPriceLoader] = useState(false);

  const { getAvailableDurations } = useBooking();

  const {
    guestList,
    resourcePrices,
    selectedDate,
    selectedSlot,
    selectedTime,
  } = useSelector((State: RootState) => State.booking);

  const fetchQuotaInfo = useCallback(
    async (payload: {
      locationId: string;
      bookingDate: string;
      selectedTime: string;
    }) => {
      if (
        !payload?.locationId ||
        !payload?.bookingDate ||
        !payload?.selectedTime
      ) {
        return null;
      }

      try {
        const response = await API_SERVICES.BOOKING.GET_QUOTE_INFO(payload);
        const responseData = handleResponse(response);

        if (responseData?.ok) {
          return responseData.data;
        }
        return null;
      } catch (err) {
        console.error("Error fetching quota info:", err);
        return null;
      }
    },
    []
  );

  const fetchResourcesPrices = async (slots: BookingFetchResponseType) => {
    if (
      !slots?.resources?.length ||
      !selectedTime ||
      !selectedDate ||
      !slots?.locationId
    )
      return;
    setPriceLoader(true);

    const resourceData = slots?.resources
      ?.map(resource => {
        const durations = getAvailableDurations(resource)?.durations?.map(d =>
          Number(d.slot)
        );
        if (!durations?.length) return null;
        return { availableDuration: durations, resourceId: resource?.id };
      })
      .filter(Boolean) as PriceSlotDurationType[];

    const postData: CalculatePriceApi = {
      bookingDate: selectedDate,
      locationId: slots?.locationId,
      participantArray: guestList.map(p => p.profileUuid),
      slotDuration: resourceData,
      startTime: selectedTime,
    };

    if (!postData?.slotDuration?.length) return null;

    return await API_SERVICES.BOOKING.CALCULATE_PRICE(postData).then(
      (response: ResourcePricesResponse) => {
        const responseData = handleResponse(response);
        dispatch(bookingSlice.actions.setResourcePrices(responseData.data));

        setPriceLoader(false);

        return responseData.data;
      }
    );
  };

  const getPriceByResourceSlot = (
    resourcePrices: ResourcePricesResponse,
    resourceId: string,
    minutesDiff: number | string
  ) => {
    const DEFAULT_PRICES = {
      guestPrice: 0,
      price: 0,
      priceConfigured: false,
      totalPriceWithTax: 0,
      vatAmount: 0,
    };

    if (!minutesDiff) return DEFAULT_PRICES;

    const durationKey = `T_${minutesDiff}`;
    const duration =
      resourcePrices?.prices?.[resourceId]?.durations?.[durationKey];

    if (!duration) return DEFAULT_PRICES;

    return {
      guestPrice: duration.participantPrice ?? 0,
      price: duration.basePrice ?? 0,
      priceConfigured: resourcePrices?.prices?.[resourceId]?.priceConfigured,
      totalPriceWithTax: duration.totalPriceWithTax ?? 0,
      vatAmount: duration.vatAmount ?? 0,
    };
  };

  const calculatePriceForUnit = ({
    activeSlot = selectedSlot?.slot,
    priceDetails,
  }: calculateArgs) => {
    let price = calculationTypes.fallbackValue;
    const slotSplitBy15 = Number(activeSlot?.slot) / 15;
    const unitPrice =
      priceDetails?.items?.[slotType.ReservationSlots]?.[
        activeSlot?.slot || calculationTypes.fallbackValue
      ];

    if (activeSlot?.type === slotType.ReservationSlots) {
      price =
        unitPrice?.specialPrice ??
        unitPrice?.price ??
        calculationTypes.fallbackValue;
    } else {
      const priceBlock = priceDetails?.items?.[slotType?.TimeSlots]?.[
        `T-${activeSlot?.slot}`
      ] || { price: 0 };

      if (!priceBlock?.specialPrice?.length) {
        const priceForOnceSlot = priceBlock?.price / slotSplitBy15;
        price =
          priceForOnceSlot *
          (Number(activeSlot?.slot) / calculationTypes.basicSlotLength);
      } else {
        const endTime = moment(selectedTime, DateFormats.Time)
          .add(activeSlot?.slot, DateFormats.Minutes)
          .format(DateFormats.Time);
        const b = { timeFrom: selectedTime, timeTo: endTime };
        const timeIncludeInSpecialPrice = priceBlock?.specialPrice?.map(
          timeRange => calculateOverlap(timeRange, b)
        );

        let timeNotIncludeInSpecialPrice: number =
          Number(activeSlot?.slot) || calculationTypes.fallbackValue;

        timeIncludeInSpecialPrice
          ?.filter(item => item.time > calculationTypes.fallbackValue)
          .map(obj => {
            const slotSplit = obj.time / calculationTypes.basicSlotLength;
            const slotPrice = obj.price / slotSplitBy15;
            price = price + slotPrice * slotSplit;

            timeNotIncludeInSpecialPrice =
              timeNotIncludeInSpecialPrice - obj.time;
          });

        if (timeNotIncludeInSpecialPrice > 0) {
          const priceForOnceSlot = priceBlock?.price / slotSplitBy15;

          price =
            price +
            (timeNotIncludeInSpecialPrice / calculationTypes.basicSlotLength) *
              priceForOnceSlot;
        }
        if (timeNotIncludeInSpecialPrice > 0 && price === 0) {
          price = Number(
            priceDetails?.items?.[slotType.TimeSlots]?.[`T-${activeSlot?.slot}`]
              ?.price
          );
        }
      }
    }

    return price;
  };

  const calculateOverlap = (
    specialSlot: specialSlotType,
    selectedSlot: selectedSlotType
  ) => {
    const timeFromA = moment(specialSlot.timeFrom, DateFormats.Time);
    const timeToA = moment(specialSlot.timeTo, DateFormats.Time);
    const timeFromB = moment(selectedSlot.timeFrom, DateFormats.Time);
    const timeToB = moment(selectedSlot.timeTo, DateFormats.Time);

    const overlapStart = moment.max(timeFromA, timeFromB);
    const overlapEnd = moment.min(timeToA, timeToB);

    const overlapDuration = moment.duration(overlapEnd.diff(overlapStart));
    const overlapMinutes = overlapDuration.asMinutes();
    const overlapPrice =
      overlapMinutes >= 0 ? specialSlot.price : calculationTypes.fallbackValue;

    return { price: overlapPrice, time: overlapMinutes };
  };

  const calculateVat = (price: number) => {
    const { hasVat = false, vatPercentage } = resourcePrices?.vat || {};

    if (!hasVat || !price) return 0;

    const percentage = Number(vatPercentage ?? calculationTypes.fallbackValue);
    if (percentage === 0) return 0;

    const vatRate = 1 + percentage / calculationTypes.percent;

    let vatAmount = 0;

    if (resourcePrices?.vat.vatHandlingType === vatHandlingType.INCLUSIVE) {
      vatAmount = price - price / vatRate;
    } else {
      vatAmount = price * (percentage / calculationTypes.percent);
    }

    return Number(vatAmount.toFixed(DECIMAL_PLACES));
  };

  const syncQuotaWithBookingSlots = (
    quotaUpdates: QuotaUpdateResponse,
    bookingSlots: BookingFetchResponseType
  ): BookingFetchResponseType => {
    return {
      ...bookingSlots,
      resources: bookingSlots.resources.map(resource => {
        const quotaUpdate = quotaUpdates[resource.id];

        if (quotaUpdate) {
          return {
            ...resource,
            quotaInfo: Array.isArray(quotaUpdate.quotaInfo)
              ? {}
              : quotaUpdate.quotaInfo,
          };
        }

        return resource;
      }),
    };
  };

  return {
    calculatePriceForUnit,
    calculateVat,
    fetchQuotaInfo,
    fetchResourcesPrices,
    getPriceByResourceSlot,
    priceLoader,
    syncQuotaWithBookingSlots,
  };
};
